import { useEffect, useState, useMemo, useCallback } from 'react';
import styles from './ProgramEdit.module.scss';
import Header from '@components/header/Header';
import { useLocation } from 'react-router-dom';
import Spinner from '@components/spinner/Spinner';
import { TextInput } from '@components/text-input/TextInput';
import RadioGroup from '@src/components/radio-group/RadioGroup';
import {
  SllProgram,
  useCreateSllProgramMutation,
  useUpdateSllProgramMutation,
  useDeleteSllProgramMutation
} from '@api/graphql-codegen';
import ImageUploadForm from '../../unit/edit/edit-content/image-form/ImageUploadForm';
import Button from '@src/components/button/Button';
import { useHistory } from 'react-router-dom';
import { EditSllLesson as EditSllLessonType } from '@src/types';
import { useToast } from '@src/hooks/useToast';
import { uploadFile } from '@src/services/File';
import { MultiLineTextInput } from '@src/components/text-input/MultiLineTextInput';
import DeleteModal from '@src/components/modal/delete-modal/DeleteModal';
import { useAWSKeys } from '@src/hooks';
import { logger } from '../../../../utils/logger';

export type EditSllLesson = EditSllLessonType;

export const PROGRAM_SHOWS = [
  { label: "Do No Display", value: "Do No Display" },
  { label: "Display on Programs Page", value: "Display on Programs Page" },
];

export enum PROGRAM_FIELDS {
  NAME = 'name',
  AVG_DURATION = 'avgDuration',
  BANNER_DESC = 'bannerDesc',
  BANNER_IMAGE = 'bannerImage',
  COUNTRY = 'country',
  DESCRIPTION = 'description',
  GRADE = 'grade',
  IS_SHOWN = 'isShown',
  LABELS = 'labels',
  LEARN_MORE_DESC = 'learnMoreDesc',
  NUMBER_OF_LESSONS = 'numberOfLessons',
  TIER = 'tier',
  ICONURL = 'iconUrl',
  HOVERTEXT = 'hoverText',
  POSITION = 'position',
}

export enum PROGRAM_ERROR_MSGS {
  NAME = 'Please add a name.',
  BANNER_IMAGE = 'Please select an image.',
  ICONURL = 'Please input a valid URL.',
  LEARN_MORE_DESC = 'Please input a valid URL.',
}

export const options = [
  {label: 'Australia', value: 'Australia'},
  {label: 'Canada', value: 'Canada'},
  {label: 'USA', value: 'USA'},
  {label: 'Canada & USA', value: 'Canada & USA'},
  {label: 'All Countries', value: 'All Countries'}
];

export const labelOptions = [
  {label: "None", value: "None"},
  {label: "New", value: "New"},
  {label: "Featured", value: "Featured"},
  {label: "Coming Soon", value: "Coming Soon"},
]

export const defaultProgram: Partial<SllProgram> = {
  name: '',
  country: '',
  bannerDesc: '',
  learnMoreDesc: '',
  isShown: false,
  description: '',
  bannerImage: '',
  grade: '',
  avgDuration: '',
  numberOfLessons: '',
  tier: '',
  labels: 'None',
  iconUrl: '',
  hoverText: '',
  position: 0,
};

const defaultErrors = {
  name: PROGRAM_ERROR_MSGS.NAME,
  bannerImage: PROGRAM_ERROR_MSGS.BANNER_IMAGE,
  iconUrl: PROGRAM_ERROR_MSGS.ICONURL,
  learnMoreDesc: PROGRAM_ERROR_MSGS.LEARN_MORE_DESC
}

function SuperProgramEdit() {
  const { state: { programData } } = useLocation<{ programData: SllProgram }>();
  const [program, setProgram] = useState<Partial<SllProgram>>(programData);
  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>(defaultErrors)
  const [imageFile, setImageFIle] = useState<File>();
  const history = useHistory();
  const { successToast, errorToast } = useToast();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { awsKeys } = useAWSKeys();

  const [createProgram, { loading: collectionCreateLoading }] = useCreateSllProgramMutation({
    onCompleted(data) {
      if (data.sllCreateProgram) {
        setProgram(data.sllCreateProgram as SllProgram)
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
      alert('Something went wrong.');
    }
  });

  const [updateProgram, { loading: collectionUpdateLoading }] = useUpdateSllProgramMutation({
    onError(e) {
      logger.debug(`[e]`, e);
      alert('Something went wrong.');
    }
  });

  const [deleteProgram, {loading: removing}] = useDeleteSllProgramMutation({
    onCompleted(data, clientOptions) {
      if (data.sllDeleteProgram) {
        successToast("Program successfully deleted!");
        setShowDeleteModal(false);
        history.replace("/lessons?tab=programs");
      } else {
        errorToast("Remove failed, Please try again!");
        setShowDeleteModal(false);
        history.goBack();
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
      errorToast("Remove failed, Please try again!");
      setShowDeleteModal(false);
      history.goBack();
    }
  })

  const handleCancel = () => {
    history.replace("/super/program-management");
  };

  const isFormValid = useMemo (() => {
    let errMsg = ''
    for (const k in errors) {
      errMsg += errors[k]
    }
    if (errMsg === '') return true
    return false
  }, [errors])

  const onChange = (key, value) => {
    if (key === PROGRAM_FIELDS.IS_SHOWN) {
      setProgram((prev) => {
        return {
          ...prev,
          [key]: value === PROGRAM_SHOWS[1].value ? true : false
        }
      })
    } else {
      setProgram((prev) => {
        return {
          ...prev,
          [key]: value
        }
      })
    }
  }

  const onUploadCollectionImage = (image: string, file: File | undefined) => {
    if (!file) {
      setProgram((prev) => {
        return {
          ...prev,
          bannerImage: ''
        }
      })
    }
    setImageFIle(file)
  }

  useEffect(() => {
    const isImageValid = () => {
      if (program?.bannerImage) {
        return true
      }
      if (program?.isShown === true && !imageFile) {
        return false
      }
      return true
    }
    setErrors({
      name: program?.name ? '' : PROGRAM_ERROR_MSGS.NAME,
      bannerImage: isImageValid() ? '' : PROGRAM_ERROR_MSGS.BANNER_IMAGE
    })
  }, [program, imageFile])

  const onSave = async () => {
    setLoading(true)
    let bannerImage;

    if (imageFile) {
      bannerImage = await uploadFile(awsKeys, imageFile, 'images');
      setProgram((prev) => {
        return {
          ...prev,
          bannerImage: bannerImage
        }
      })
    }


    if (!program.id) {
      const newProgram = await createProgram({
        variables: {
          input: {
            ...program,
            name: program.name ?? '',
            country: program.country ?? '',
            isShown: program.isShown ?? false,
            labels: program.labels  ?? labelOptions[0].value,
            position: Number(program.position) ?? 1,
            bannerImage,
          },
        }
      });
      const newCollectionnId = newProgram.data?.sllCreateProgram?.id;
      if (newCollectionnId) {
        successToast('Program has been created successfully!');
        history.push({ pathname: `/super/program-management/edit/${newCollectionnId}`, state: { programData: newProgram.data?.sllCreateProgram } });

      } else {
        errorToast('Failed to create a program!');
      }
    } else {
      const update = await updateProgram({
        variables: {
          input: {
            id: program.id,
            name: program.name ?? '',
            country: program.country ?? '',
            isShown: program.isShown ?? false,
            bannerDesc: program.bannerDesc,
            description: program.description,
            grade: program.grade,
            learnMoreDesc: program.learnMoreDesc,
            avgDuration: program.avgDuration,
            numberOfLessons: program.numberOfLessons,
            tier: program.tier,
            labels: program.labels  ?? labelOptions[0].value,
            iconUrl: program.iconUrl,
            hoverText: program.hoverText,
            position: Number(program.position) ?? 1,
            bannerImage,
          },
        }
      });
      if (update.data?.sllUpdateProgram) {
        successToast('Program has been updated successfully!');
      } else {
        successToast('Failed to update a program!');
      }
    }
    setLoading(false);
  }

  const handleDeleteProgram = () => {
    setShowDeleteModal(true);
  }

  const handleRemoveProgram = useCallback(() => {
    if (program.id) {
      deleteProgram({
        variables: {
          input: {
            id: program.id
          }
        }
      })
    }

  }, [program.id, deleteProgram])

  return (
    <>
      <Header />
      <div className={styles.page}>
        <div className={styles.list}>
          <div className={styles.programName}>Program Overview</div>
        </div>
        <div className={styles.content}>
          <div className={styles.row}>
            <div className={styles.actions}>
              <div className={styles.title}>
              Program Overview
              </div>
              <div className={styles.buttons}>
                <Button
                  className={styles.saveButton}
                  disabled={collectionCreateLoading || collectionUpdateLoading || !isFormValid || isLoading}
                  isLoading={collectionCreateLoading || collectionUpdateLoading || isLoading}
                  leftIcon={isLoading ? <Spinner width={20} height={20} /> : undefined}
                  onClick={onSave} size={'small'} label={'SAVE'}
                />
                <Button className={styles.cancelButton} disabled={collectionUpdateLoading || collectionCreateLoading || isLoading} onClick={handleCancel} size={'small'} label={program.id ? 'Go Back' : 'Cancel'} />
              </div>
            </div>
          </div>
          {program ? (
            <>
              <div className={styles.row} >
                <div className={`${styles.subtitle}`}>Program Name</div>
                <TextInput
                  hasError={!!errors?.name}
                  subTitle={errors?.name}
                  label={''}
                  id={'name'}
                  placeholder={'Add Program name'}
                  value={program?.name ?? ''}
                  onChange={(value) => onChange(PROGRAM_FIELDS.NAME, value)}
                />
              </div>
              <div className={styles.row} >
                <div className={styles.subtitle}>Program Country</div>
                <RadioGroup
                  value={program.country || 'All Countries'}
                  options={options}
                  onChange={(value: string) => onChange(PROGRAM_FIELDS.COUNTRY, value)}
                  name={''}
                  hasError={false}
                  subTitle={''}
                />
              </div>
              <div className={styles.row} >
                <div className={`${styles.subtitle}`}>Program Icon URL</div>
                <TextInput
                  label={''}
                  id={'icon-link'}
                  hasError={!!errors?.iconUrl}
                  subTitle={errors?.iconUrl}
                  placeholder={'This url should be link to LordIcon.'}
                  value={program?.iconUrl ?? ''}
                  onChange={(value) => onChange(PROGRAM_FIELDS.ICONURL, value)}
                />
              </div>
              <div className={styles.row} >
                <div className={`${styles.subtitle}`}>Program Banner Description</div>
                <TextInput
                  label={''}
                  id={'banner-description'}
                  placeholder={'This description appears in the purple banner'}
                  value={program?.bannerDesc ?? ''}
                  onChange={(value) => onChange(PROGRAM_FIELDS.BANNER_DESC, value)}
                />
              </div>
              <div className={styles.row} >
                <div className={`${styles.subtitle}`}>'Learn More' Button - Program Description</div>
                <TextInput
                  label={''}
                  id={'learn-more-link'}
                  placeholder={'This url linked on the learn more button in the purple banner'}
                  value={program?.learnMoreDesc ?? ''}
                  onChange={(value) => onChange(PROGRAM_FIELDS.LEARN_MORE_DESC, value)}
                />
              </div>
              <div className={styles.row} >
                <div className={`${styles.subtitle}`}>Dropdown Menu (i) Program Description</div>
                <TextInput
                  label={''}
                  id={'hint-description'}
                  placeholder={'This description appears in the hover of DropDown Menu'}
                  value={program?.hoverText ?? ''}
                  onChange={(value) => onChange(PROGRAM_FIELDS.HOVERTEXT, value)}
                />
              </div>
              <div className={styles.row} >
                <div className={styles.additionalBox}>
                  <div className={`${styles.subtitle}`}>Additional Program Information</div>
                  <div className={styles.boxContainer}>
                    <div className={styles.subBox}>
                      <TextInput
                        label={'Age Group'}
                        id={'age-group'}
                        placeholder={'Grade 1 ~ Grade 12'}
                        value={program?.grade ?? ''}
                        onChange={(value) => onChange(PROGRAM_FIELDS.GRADE, value)}
                        className={styles.additionalInput}
                      />
                      <TextInput
                        label={'Average Duration'}
                        id={'duration'}
                        placeholder={'duration'}
                        value={program?.avgDuration ?? ''}
                        onChange={(value) => onChange(PROGRAM_FIELDS.AVG_DURATION, value)}
                        className={styles.additionalInput}
                      />
                      <TextInput
                        label={'How many lessons?'}
                        id={'lesson-count'}
                        placeholder={'lessons'}
                        value={program?.numberOfLessons ?? ''}
                        onChange={(value) => onChange(PROGRAM_FIELDS.NUMBER_OF_LESSONS, value)}
                        className={styles.additionalInput}
                      />
                      <TextInput
                        label={'Tier'}
                        id={'tier'}
                        placeholder={'Tier'}
                        value={program?.tier ?? ''}
                        onChange={(value) => onChange(PROGRAM_FIELDS.TIER, value)}
                        className={styles.additionalInput}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.row} style={{width: 100}}>
                <div className={`${styles.subtitle}`}>Position</div>
                <TextInput
                  label={''}
                  id={'position'}
                  placeholder={'This position is order of the programs.'}
                  value={program?.position ?? 0}
                  type='number'
                  onChange={(value) => onChange(PROGRAM_FIELDS.POSITION, value)}
                />
              </div>
              <div className={styles.row} >
                <div className={styles.subtitle}>Display Program on Programs Page</div>
                <div className={styles.radioWrapper}>
                  <RadioGroup
                    value={program.isShown ? PROGRAM_SHOWS[1].value : PROGRAM_SHOWS[0].value}
                    options={PROGRAM_SHOWS}
                    onChange={(value) => onChange(PROGRAM_FIELDS.IS_SHOWN, value)}
                    name={''}
                    style={{ display: 'flex', gap: '1rem' }}
                  />
                </div>
              </div>
              {program.isShown &&
                <>
                  <div className={styles.row} >
                    <div className={`${styles.subtitle}`}>Program Description</div>
                    <MultiLineTextInput
                      allowSpecialCharacters={false}
                      label={''}
                      id={'program-description'}
                      placeholder={'This description appears on the Programs Page'}
                      value={program?.description ?? ''}
                      onChange={(value) => onChange(PROGRAM_FIELDS.DESCRIPTION, value)}
                    />
                  </div>
                  <div className={styles.row}>
                    <div className={styles.subtitle}>
                    {'Programs Page Banner Image - (Dimensions {x} x {y})'}
                    </div>
                    <ImageUploadForm
                      url={program.bannerImage || ''}
                      onChange={onUploadCollectionImage}
                      hasError={!!errors?.bannerImage}
                      subTitle={errors?.bannerImage}
                    />
                  </div>
                  <div className={styles.row} >
                    <div className={styles.additionalBox}>
                      <div className={styles.subtitle}>Labels</div>
                      <RadioGroup
                        value={program.labels ?? labelOptions[0].value}
                        options={labelOptions}
                        onChange={(value) => onChange(PROGRAM_FIELDS.LABELS, value)}
                        name={''}
                        hasError={false}
                        subTitle={""}
                      />
                    </div>
                  </div>
                </>
              }
              <hr className={styles.horizDivider} />
              <div className={styles.row}>
                <div className={styles.warningText}>Warning</div>
                <Button label='DELETE' color='red' className={styles.deleteButton} onClick={handleDeleteProgram} />
              </div>
            </>
          ) : null}
        </div>
      </div>
      {showDeleteModal && <DeleteModal open={showDeleteModal} onClose={() => {setShowDeleteModal(false)}} loading={removing} onDelete={handleRemoveProgram} message={`Are you sure you want to delete ${program?.name}?`} cancelLabel={'NO'} deleteLabel={'DELETE'}/>}
    </>
  );
}

const ProgramEdit = () => {
  return (
    <SuperProgramEdit />
  )
};

export default ProgramEdit;
